import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { landingAPI } from '../utils/api';
import HeroEditor from './Admin/LandingManagerTabs/HeroEditor';
import AboutEditor from './Admin/LandingManagerTabs/AboutEditor';
import StatsEditor from './Admin/LandingManagerTabs/StatsEditor';
import CTAEditor from './Admin/LandingManagerTabs/CTAEditor';
import ContactFooterEditor from './Admin/LandingManagerTabs/ContactFooterEditor';
import { tField } from '../utils/bilingualHelper';
import { useTranslation } from 'react-i18next';

// Default bilingual structure for fresh installs
const defaultBilingual = (en = '') => ({ en, zh: '' });

const defaultContent = {
  heroHeadline:        defaultBilingual("Where\nBallet\nBecomes Life."),
  heroSubtext:         defaultBilingual("ETS brings the full dance academy experience to your fingertips."),
  heroImageUrl:        '',
  heroLabel:           defaultBilingual("Classical Ballet"),
  heroBadge1:          defaultBilingual("10+\nYears of Dance"),
  heroBadge2:          defaultBilingual("4.9 / 5.0\nStudent Rating"),
  aboutImageUrl:       '',
  aboutBadgeBig:       '10+',
  aboutBadgeSmall:     defaultBilingual("Years\nof dance"),
  aboutOverline:       defaultBilingual("About ETS Academy"),
  aboutTitleRegular:   defaultBilingual("A Platform Built "),
  aboutTitleGold:      defaultBilingual("For Dancers"),
  aboutSubtext:        defaultBilingual("Every Talent Shines Academy is the complete dance management platform."),
  aboutFeatures: [
    { emoji: '📊', title: defaultBilingual('Attendance Tracking'), desc: defaultBilingual('Real-time Present / Absent / Late marking with monthly percentage graphs.') },
    { emoji: '🎯', title: defaultBilingual('Personal Objectives'),  desc: defaultBilingual('Set skill goals and watch progress bars move from 0 to 100% session by session.') },
    { emoji: '⭐', title: defaultBilingual('Achievement Badges'),   desc: defaultBilingual('Earn Star Performer, Perfect Attendance, and 8 more milestone badges.') },
    { emoji: '📝', title: defaultBilingual('Tutor Notes'),          desc: defaultBilingual('Personalised, dated feedback from your tutor after every class.') }
  ],
  stats: [
    { number: '12,000+', label: defaultBilingual('Active Dancers') },
    { number: '150+',    label: defaultBilingual('Partner Academies') },
    { number: '45+',     label: defaultBilingual('Expert Tutors') },
    { number: '9',       label: defaultBilingual('Dance Styles') }
  ],
  danceStyles: [
    { emoji: '🩰', name: { en: 'Ballet',       zh: '芭蕾' } },
    { emoji: '👟', name: { en: 'Hip Hop',      zh: '嘻哈' } },
    { emoji: '💃', name: { en: 'Salsa',        zh: '騷莎' } },
    { emoji: '🔥', name: { en: 'Breakdance',   zh: '霹靂舞' } },
    { emoji: '✨', name: { en: 'Contemporary', zh: '現代舞' } },
    { emoji: '🎭', name: { en: 'Kathak',       zh: '卡塔克' } },
    { emoji: '🎶', name: { en: 'Jazz',         zh: '爵士' } },
    { emoji: '🌊', name: { en: 'Lyrical',      zh: '抒情舞' } }
  ],
  ctaOverline:         defaultBilingual("Join ETS Academy Today"),
  ctaTitleRegular:     defaultBilingual("Your Dance Story\nStarts "),
  ctaTitleItalic:      defaultBilingual("Today"),
  ctaSubtext:          defaultBilingual("Join 12,000+ dancers training smarter, improving faster, and loving every step."),
  ctaPrimaryBtnText:   defaultBilingual("Start Free Trial →"),
  ctaSecondaryBtnText: defaultBilingual("Explore Classes"),
  ctaNote:             defaultBilingual("14-day free trial · No credit card required · Cancel anytime"),

  // Contact & Footer
  contactOverline:     defaultBilingual("Contact Info"),
  contactTitleRegular: defaultBilingual("Let's get you "),
  contactTitleItalic:  defaultBilingual("dancing"),
  contactLocation:     defaultBilingual("Hong Kong · Multiple Locations"),
  contactPhone:        defaultBilingual("+852 6681 5305"),
  contactEmail:        defaultBilingual("info@etsacademy.com"),
  contactHours:        defaultBilingual("Mon–Sat: 7am–9pm · Sun: 9am–6pm"),
  footerTagline:       defaultBilingual("The complete dance academy management platform. Move, grow, and perform your best with Every Talent Shines Academy.")
};

// Ensures a field from the DB is in { en, zh } format (backward compat)
const normalizeBilingual = (val, fallback = '') => {
  if (!val) return defaultBilingual(fallback);
  if (typeof val === 'string') return { en: val, zh: '' }; // old plain string → migrate
  return { en: val.en || '', zh: val.zh || '' };
};

export default function LandingManager() {
  const { t } = useTranslation();
  const [content, setContent] = useState(defaultContent);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [heroImagePreview, setHeroImagePreview] = useState('');
  const [aboutImageFile, setAboutImageFile] = useState(null);
  const [aboutImagePreview, setAboutImagePreview] = useState('');
  const [activeTab, setActiveTab] = useState('hero');

  useEffect(() => {
    landingAPI.getContent()
      .then(res => {
        const d = res.data;
        // Normalize all fields to bilingual format (supports old plain-string DB docs)
        setContent({
          ...defaultContent,
          ...d,
          heroHeadline:        normalizeBilingual(d.heroHeadline),
          heroSubtext:         normalizeBilingual(d.heroSubtext),
          heroLabel:           normalizeBilingual(d.heroLabel),
          heroBadge1:          normalizeBilingual(d.heroBadge1),
          heroBadge2:          normalizeBilingual(d.heroBadge2),
          aboutBadgeSmall:     normalizeBilingual(d.aboutBadgeSmall),
          aboutOverline:       normalizeBilingual(d.aboutOverline),
          aboutTitleRegular:   normalizeBilingual(d.aboutTitleRegular),
          aboutTitleGold:      normalizeBilingual(d.aboutTitleGold),
          aboutSubtext:        normalizeBilingual(d.aboutSubtext),
          ctaOverline:         normalizeBilingual(d.ctaOverline),
          ctaTitleRegular:     normalizeBilingual(d.ctaTitleRegular),
          ctaTitleItalic:      normalizeBilingual(d.ctaTitleItalic),
          ctaSubtext:          normalizeBilingual(d.ctaSubtext),
          ctaPrimaryBtnText:   normalizeBilingual(d.ctaPrimaryBtnText),
          ctaSecondaryBtnText: normalizeBilingual(d.ctaSecondaryBtnText),
          ctaNote:             normalizeBilingual(d.ctaNote),
          contactOverline:     normalizeBilingual(d.contactOverline),
          contactTitleRegular: normalizeBilingual(d.contactTitleRegular),
          contactTitleItalic:  normalizeBilingual(d.contactTitleItalic),
          contactLocation:     normalizeBilingual(d.contactLocation),
          contactPhone:        normalizeBilingual(d.contactPhone),
          contactEmail:        normalizeBilingual(d.contactEmail),
          contactHours:        normalizeBilingual(d.contactHours),
          footerTagline:       normalizeBilingual(d.footerTagline),
          aboutFeatures: (d.aboutFeatures || defaultContent.aboutFeatures).map(f => ({
            emoji: f.emoji || '',
            title: normalizeBilingual(f.title),
            desc:  normalizeBilingual(f.desc)
          })),
          stats: (d.stats || defaultContent.stats).map(s => ({
            number: s.number || '',
            label:  normalizeBilingual(s.label)
          })),
          danceStyles: (d.danceStyles || defaultContent.danceStyles).map(s => ({
            emoji: s.emoji || '',
            name:  normalizeBilingual(tField(s.name))
          }))
        });
      })
      .catch(() => setError('Failed to load content.'))
      .finally(() => setLoading(false));
  }, []);

  // Handler for simple (non-bilingual) fields
  const handleChange = (e) => {
    setContent(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Handler for bilingual fields — called from BilingualField
  const handleBilingualChange = (fieldName, lang, value) => {
    setContent(prev => ({
      ...prev,
      [fieldName]: { ...prev[fieldName], [lang]: value }
    }));
  };

  const handleFeatureChange = (index, field, value) => {
    setContent(prev => {
      const newFeatures = [...prev.aboutFeatures];
      newFeatures[index] = { ...newFeatures[index], [field]: value };
      return { ...prev, aboutFeatures: newFeatures };
    });
  };

  const handleStatChange = (index, field, value) => {
    setContent(prev => {
      const updated = [...prev.stats];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, stats: updated };
    });
  };
  const addStat    = () => setContent(prev => ({ ...prev, stats: [...prev.stats, { number: '', label: defaultBilingual() }] }));
  const removeStat = (i) => setContent(prev => ({ ...prev, stats: prev.stats.filter((_, idx) => idx !== i) }));

  const handleStyleChange = (index, field, value) => {
    setContent(prev => {
      const updated = [...prev.danceStyles];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, danceStyles: updated };
    });
  };
  const addStyle    = () => setContent(prev => ({ ...prev, danceStyles: [...prev.danceStyles, { emoji: '', name: defaultBilingual() }] }));
  const removeStyle = (i) => setContent(prev => ({ ...prev, danceStyles: prev.danceStyles.filter((_, idx) => idx !== i) }));

  const handleImageChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'hero') { setHeroImageFile(file); setHeroImagePreview(reader.result); }
        else { setAboutImageFile(file); setAboutImagePreview(reader.result); }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      let finalHeroImageUrl  = content.heroImageUrl;
      let finalAboutImageUrl = content.aboutImageUrl;

      if (heroImageFile) {
        const formData = new FormData();
        formData.append('photo', heroImageFile);
        const uploadRes = await landingAPI.uploadHeroImage(formData);
        finalHeroImageUrl = uploadRes.data.url;
      }
      if (aboutImageFile) {
        const formData = new FormData();
        formData.append('photo', aboutImageFile);
        const uploadRes = await landingAPI.uploadAboutImage(formData);
        finalAboutImageUrl = uploadRes.data.url;
      }

      const payload = {
        ...content,
        heroImageUrl:  finalHeroImageUrl,
        aboutImageUrl: finalAboutImageUrl
        // bilingual objects and arrays are sent as-is (axios sends as JSON)
      };

      await landingAPI.updateContent(payload);
      setSuccess('Landing page content updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setHeroImageFile(null);
      setAboutImageFile(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update content.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Layout title={t('landingManager.title')}><div className="dash-loading">{t('landingManager.loading')}</div></Layout>;

  return (
    <Layout title={t('landingManager.title')}>
      <div className="dash-page-header">
        <div>
          <h1 className="dash-page-title">{t('landingManager.title')}</h1>
          <p className="text-sm text-muted">
            {t('landingManager.subtitlePart1')}<strong>{t('landingManager.subtitleEn')}</strong>{t('landingManager.subtitleAnd')}<strong>{t('landingManager.subtitleZh')}</strong>
            {t('landingManager.subtitlePart2')}<em>{t('landingManager.subtitleTranslateBtn')}</em>{t('landingManager.subtitlePart3')}
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 24, borderBottom: '1px solid var(--border)', paddingBottom: 16, overflowX: 'auto' }}>
        {[
          { key: 'hero',  label: t('landingManager.tabHero') },
          { key: 'about', label: t('landingManager.tabAbout') },
          { key: 'stats', label: t('landingManager.tabStats') },
          { key: 'cta',   label: t('landingManager.tabCta') },
          { key: 'contact', label: t('landingManager.tabContact') }
        ].map(tab => (
          <button
            key={tab.key}
            className={`btn ${activeTab === tab.key ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setActiveTab(tab.key)}
            style={{ whiteSpace: 'nowrap' }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit}>
        {error && <div className="form-status-box error mb-4">{error}</div>}

        {activeTab === 'hero' && <HeroEditor content={content} handleBilingualChange={handleBilingualChange} handleChange={handleChange} handleImageChange={handleImageChange} heroImagePreview={heroImagePreview} />}
        {activeTab === 'about' && <AboutEditor content={content} handleBilingualChange={handleBilingualChange} handleChange={handleChange} handleImageChange={handleImageChange} handleFeatureChange={handleFeatureChange} aboutImagePreview={aboutImagePreview} />}
        {activeTab === 'stats' && <StatsEditor content={content} handleStatChange={handleStatChange} addStat={addStat} removeStat={removeStat} handleStyleChange={handleStyleChange} addStyle={addStyle} removeStyle={removeStyle} />}
        {activeTab === 'cta' && <CTAEditor content={content} handleBilingualChange={handleBilingualChange} />}
        {activeTab === 'contact' && <ContactFooterEditor content={content} handleBilingualChange={handleBilingualChange} />}

        <div style={{ position: 'sticky', bottom: 20, background: 'rgba(255,255,255,0.95)', padding: '16px 24px', borderRadius: 12, border: '1px solid var(--border)', boxShadow: '0 4px 30px rgba(0,0,0,0.08)', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 100, backdropFilter: 'blur(10px)' }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            {success && <span style={{ color: '#27ae60' }}>✓ {success}</span>}
            {error   && <span style={{ color: '#c0392b' }}>⚠ {error}</span>}
          </div>
          <button type="submit" className="btn btn-primary btn-lg" disabled={saving}>
            {saving ? t('landingManager.saving') : t('landingManager.saveAll')}
          </button>
        </div>
      </form>
    </Layout>
  );
}
